import '../styles/LeadsSavedListSelector.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const SIGNED_IN_CONTEXT = 'Signed in researcher';
const SIGNED_IN_IDENTITY = 'Lead researcher account';
const EXTRACTION_HREF = '/extraction';

const STATUS_LABELS = {
  complete: 'Complete',
  running: 'Running',
  failed: 'Failed',
};

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatDate(value) {
  if (!value || typeof value !== 'string') return null;
  const parts = value.slice(0, 10).split('-');
  if (parts.length !== 3) return value;
  const monthIndex = Number(parts[1]) - 1;
  const day = Number(parts[2]);
  if (monthIndex < 0 || monthIndex > 11 || !day) return value;
  return `${day} ${MONTHS[monthIndex]} ${parts[0]}`;
}

function formatTotal(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return String(value);
}

function ArrowGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M2 8h11M9 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
    </svg>
  );
}

function SavedListRows({ runs, selectedRunId, onSelect }) {
  return (
    <div className="lsl-rows" data-subcomponent="SavedListRows">
      <div className="lsl-cols-head" aria-hidden="true">
        <span className="lsl-label">Saved list</span>
        <span className="lsl-label lsl-col-date">Created</span>
        <span className="lsl-label lsl-col-total">Result total</span>
      </div>
      <ul className="lsl-list" role="listbox" aria-label="Saved lead lists">
        {runs.map((run) => {
          const active = run.id === selectedRunId;
          const date = formatDate(run.createdAt);
          const total = formatTotal(run.resultTotal);
          const status = STATUS_LABELS[run.status] || run.status;
          return (
            <li key={run.id} role="none" style={{ display: 'contents' }}>
              <button
                type="button"
                role="option"
                aria-selected={active}
                className="lsl-row"
                onClick={() => onSelect(run.id)}
              >
                <span className="lsl-row-title">
                  <span className="lsl-marker" aria-hidden="true" />
                  {run.title}
                </span>
                <span className="lsl-row-date">
                  {date ? <span className="lsl-date-value">{date}</span> : <span className="lsl-date-value">Date unavailable</span>}
                  {status ? <span>{status}</span> : null}
                </span>
                <span className="lsl-row-total">
                  {total != null ? total : '—'}
                  <span className="lsl-signal lsl-signal--idle" aria-hidden="true" />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NoListsEmptyState() {
  return (
    <div className="lsl-status" data-subcomponent="NoListsEmptyState">
      <span className="lsl-label">No saved lists</span>
      <p className="lsl-status-msg">
        <strong>You have no saved lead lists yet.</strong>
      </p>
      <p className="lsl-status-sub">
        Run an extraction to build a lead list. Completed runs are saved here and available the next time you sign in.
      </p>
      <a className="lsl-link lsl-link--signal" href={EXTRACTION_HREF}>
        Go to Extraction
        <ArrowGlyph />
      </a>
    </div>
  );
}

function SelectedListTotal({ run, ready }) {
  const total = run ? formatTotal(run.resultTotal) : null;
  return (
    <div className="lsl-selected-total" data-subcomponent="SelectedListTotal" aria-live="polite">
      <span className={ready && run ? 'lsl-signal' : 'lsl-signal lsl-signal--idle'} aria-hidden="true" />
      <div className="lsl-selected-body">
        <span className="lsl-selected-what">
          {run ? `Selected · ${run.title}` : 'No list selected'}
        </span>
        <span className="lsl-selected-figure">
          {ready && total != null ? total : '—'}
          <span className="lsl-label">Leads</span>
        </span>
      </div>
    </div>
  );
}

function AccessContextLine() {
  return (
    <div className="lsl-access" data-subcomponent="AccessContextLine">
      <span className="lsl-access-flag">
        <span className="lsl-access-dot" aria-hidden="true" />
        Sign-in required
      </span>
      <span className="lsl-access-who">
        <strong>{SIGNED_IN_CONTEXT}</strong>
        <span className="lsl-access-route"> — {SIGNED_IN_IDENTITY}</span>
      </span>
    </div>
  );
}

function LeadsSavedListSelector({ selectedRunId: controlledRunId, onSelectRun }) {
  const { data: runs, loading, error, refresh } = useProjectCollection('extraction_runs');
  const { data: leads } = useProjectCollection('leads');
  const [localRunId, setLocalRunId] = useState(null);
  const selectedRunId = controlledRunId !== undefined ? controlledRunId : localRunId;
  const setSelectedRunId = useCallback((id) => {
    setLocalRunId(id);
    if (onSelectRun) onSelectRun(id);
  }, [onSelectRun]);
  const listRef = useRef(null);

  const pendingRuns = useMemo(
    () => {
      const leadCounts = new Map();
      (leads || []).forEach((lead) => {
        if (!lead || !lead.runId) return;
        leadCounts.set(lead.runId, (leadCounts.get(lead.runId) || 0) + 1);
      });
      return (runs || []).map((run) => {
        const derived = leadCounts.get(run.id);
        const declared = formatTotal(run.resultTotal);
        return {
          ...run,
          resultTotal: declared != null ? run.resultTotal : (derived || 0),
        };
      });
    },
    [runs, leads]
  );

  useEffect(() => {
    if (!pendingRuns.length) {
      if (selectedRunId !== null) setSelectedRunId(null);
      return;
    }
    const stillPresent = pendingRuns.some((run) => run.id === selectedRunId);
    if (!stillPresent) setSelectedRunId(pendingRuns[0].id);
  }, [pendingRuns, selectedRunId]);

  const selectedRun = pendingRuns.find((run) => run.id === selectedRunId) || null;

  const handleKeyDown = (event) => {
    const index = pendingRuns.findIndex((run) => run.id === selectedRunId);
    if (index < 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = event.key === 'ArrowDown'
        ? (index + 1) % pendingRuns.length
        : (index - 1 + pendingRuns.length) % pendingRuns.length;
      setSelectedRunId(pendingRuns[next].id);
      const buttons = listRef.current ? listRef.current.querySelectorAll('[role="option"]') : [];
      if (buttons[next]) buttons[next].focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : pendingRuns.length - 1;
      setSelectedRunId(pendingRuns[next].id);
    }
  };

  const hasError = Boolean(error);
  const showLoading = loading && !pendingRuns.length;
  const showPopulated = !hasError && pendingRuns.length > 0;
  const showEmpty = !hasError && !loading && pendingRuns.length === 0;

  const errorMessage = error && typeof error === 'object' && error.message
    ? error.message
    : 'The saved lists are unreachable right now. Retry to request them again; nothing has been removed.';

  return (
    <section className="lsl-root" aria-labelledby="lsl-title">
      <header className="lsl-head">
        <div className="lsl-headtext">
          <span className="lsl-label">02 · Saved lead lists</span>
          <h2 className="lsl-title" id="lsl-title">Saved lists</h2>
        </div>
        <AccessContextLine />
      </header>

      <div id="lsl-saved-lists-region" ref={listRef} onKeyDown={handleKeyDown}>
        {hasError ? (
          <div className="lsl-status lsl-status--error" role="alert" data-subcomponent="LoadErrorRegion">
            <span className="lsl-label lsl-status-flag">Load failed</span>
            <p className="lsl-status-msg"><strong>Saved lead lists could not be loaded.</strong></p>
            <p className="lsl-status-sub">{errorMessage}</p>
            <button type="button" className="lsl-link" aria-controls="lsl-saved-lists-region" onClick={() => { refresh(); }}>
              Retry loading lists
            </button>
          </div>
        ) : null}

        {!hasError && !showPopulated && !showEmpty ? (
          <div className="lsl-cols-head" aria-hidden="true">
            <span className="lsl-label">Saved list</span>
            <span className="lsl-label lsl-col-date">Created</span>
            <span className="lsl-label lsl-col-total">Result total</span>
          </div>
        ) : null}

        {showLoading ? (
          <div className="lsl-status" aria-busy="true" aria-live="polite">
            <span className="lsl-label">Loading saved lists</span>
            <div className="lsl-loading-bars" aria-hidden="true">
              <span className="lsl-loading-bar" />
              <span className="lsl-loading-bar" />
              <span className="lsl-loading-bar" />
            </div>
            <p className="lsl-status-sub">Reading your saved lead lists.</p>
          </div>
        ) : null}

        {showPopulated ? (
          <SavedListRows runs={pendingRuns} selectedRunId={selectedRunId} onSelect={setSelectedRunId} />
        ) : null}

        {showEmpty ? <NoListsEmptyState /> : null}
      </div>

      <footer className="lsl-foot">
        <SelectedListTotal run={selectedRun} ready={showPopulated} />
        <div className="lsl-foot-action">
          <a className="lsl-link" href={EXTRACTION_HREF}>
            New extraction
            <ArrowGlyph />
          </a>
        </div>
      </footer>
    </section>
  );
}

export default LeadsSavedListSelector;